import React, { useEffect, useState } from "react";
import useCeo from "../../hooks/useCEO";
import { useUnidades } from "../../hooks/test/useUnidades";
import { Spinner } from "../../components/Spinner";
import moment from "moment";
import 'moment/locale/es-mx';
import Swal from "sweetalert2";
import DocPDFReporteFinalEvento from "../../models/DocPDFReporteFinalEvento";
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from "@tanstack/react-table";
import Paginacion from "../../components/customComponents/Paginacion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv } from "@fortawesome/free-solid-svg-icons";
import { exportToCSV } from "../../components/customComponents/exportToCSV";

export const ReporteEventosSeguridad = () => {

  useCeo({
    title: "Reporte eventos de seguridad",
    description: "Página de reporte eventos de seguridad",
  });

  moment.locale('es-mx');

  const {
    getRegistroSeguridadEventosByDate,
    getRegistrosSeguridadPageFinalizados,
    getSeguimientoRegistrosSeguridad,
  } = useUnidades();

  const [formInfo, setFormInfo] = useState({
    fechaInicial: "",
    fechaFinal: "",
  });
  const [registroEventosSeguridad, setRegistroEventosSeguridad] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginado, setPaginado] = useState([]);
  const [busqueda, setBusqueda] = useState("");

  useEffect(async () => {
    await loadData();
  }, []);

  const loadData = async (pagina = 1, busquedaTexto = "", fecha_inicio = "", fecha_fin = "") => {
    setLoading(true);

    let r = await getRegistrosSeguridadPageFinalizados(pagina, busquedaTexto, fecha_inicio, fecha_fin);

    if (r.ok) {

      setPaginado({
        page: r.data.paginacion.pagina || 1,
        limit: r.data.paginacion.totalRegistros || 5,
        previousPage: r.data.paginacion.previousPage || null,
        nextPage: r.data.paginacion.nextPage || null,
        total: r.data.paginacion.totalRegistros || 0,
        totalPaginas: r.data.paginacion.totalPaginas || 1,
      });

      const eventos = r.data.eventos;

      // Crear un array de promesas
      const promesas = eventos.map(async ({ evento_seguridad_id }) => {
        const re = await getSeguimientoRegistrosSeguridad(evento_seguridad_id);
        return re.ok ? re.evento : null;
      });

      // Esperar a que todas se resuelvan
      const resultados = await Promise.all(promesas);

      // Filtrar nulls (por si alguna petición falló)
      const eventosValidos = resultados.filter(e => e !== null);
      debugger
      // Guardar el arreglo completo
      setRegistroEventosSeguridad(eventosValidos);
    } else {
      setRegistroEventosSeguridad([]);
    }

    setLoading(false);
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor('folio', {
      header: 'FOLIO',
    }),
    columnHelper.accessor(row => row.evento?.evento || "", {
      id: 'evento',
      header: 'EVENTO',
    }),
    columnHelper.accessor('createdAt', {
      header: 'FECHA',
      cell: info => new Date(info.getValue()).toLocaleString(),
    }),
    columnHelper.accessor(row => row.tipo_unidad_id === 1 ? row.unidad.unidad_numero : row.unidad_vin || "", {
      id: 'unidad',
      header: 'UNIDAD',
    }),
    columnHelper.accessor(row => row.tipo_unidad_id === 1 ? row.unidad?.negocio?.negocio_nombre : row.unidad?.cliente || "", {
      id: 'cliente',
      header: 'CLIENTE',
    }),
    columnHelper.accessor(row => row.estado?.entidad_federativa || "", {
      id: 'estado',
      header: 'ESTADO',
    }),
    columnHelper.accessor(row => row.municipio?.municipio || "", {
      id: 'municipio',
      header: 'MUNICIPIO',
    }),
    columnHelper.display({
      id: 'reporte final',
      header: 'REPORTE FINAL',
      cell: info => {
        const row = info.row.original; // <- Aquí accedes al dato completo de la fila

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <DocPDFReporteFinalEvento
              registroSeguridadEventos={registroEventosSeguridad}
              unidadEventSeguridad={row.evento_seguridad_id}
              numeroUnidad={
                row.tipo_unidad_id === 1
                  ? row.unidad?.unidad_numero
                  : row.unidad_vin
              }
            />
          </div>
        );
      }
    })
  ];

  const table = useReactTable({
    data: registroEventosSeguridad,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Manejar el cambio de página
  const handlePageChange = async (newPage) => {
    loadData(newPage, busqueda, formInfo.fechaInicial, formInfo.fechaFinal);
  };

  const generarReporte = async () => {
    setLoading(true);
    await loadData(1, busqueda, formInfo.fechaInicial, formInfo.fechaFinal);
  }

  const handleExportCSV = async () => {
    try {
      let r = await getRegistrosSeguridadPageFinalizados("", busqueda, formInfo.fechaInicial, formInfo.fechaFinal);
      if (r.ok) {
        const eventos = r.data;

        if (!eventos || eventos.length === 0) {
          return Swal.fire({
            title: "No hay datos para exportar",
            icon: "info",
            timer: 2000,
            showConfirmButton: false,
          });
        }

        exportToCSV({
          data: eventos,
          filename: 'Eventos_Seguridad',
          headers: [
            { title: 'FOLIO', key: 'folio' },
            { title: 'EVENTO', key: 'evento_seguridad' },
            { title: 'FECHA', key: 'createdAt' },
            { title: 'UNIDAD', key: 'unidad_identificador' },
            { title: 'PLACAS', key: 'unidad_placas' },
            { title: 'ESTADO', key: 'entidad_federativa' },
            { title: 'MUNICIPIO', key: 'municipio' },
          ]
        });
      } else {
        Swal.fire({
          title: r.msg ? r.msg : "Error al generar reporte",
          icon: "info",
          timer: 2500,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error al exportar CSV:", error);
      Swal.fire({
        title: "Error inesperado",
        text: "No se pudo generar el reporte",
        icon: "error",
        timer: 2500,
        showConfirmButton: false,
      });
    }
  };


  return (
    <>
      {loading && <Spinner />}
      <div className="row" id="eventos-seguridad">
        <div className="col-2 mb-3">
          <label className="form-label">FECHA INICIAL</label>
          <input
            type="date"
            data-date-format="DD MMMM YYYY"
            className="form-control"
            selected={formInfo.fechaInicial}
            onChange={(date) => setFormInfo({ ...formInfo, fechaInicial: date.target.value })}
            style={{ width: "100%" }}
          // date.getFullYear()+'-',date.get
          />
        </div>
        <div className="col-2 mb-3">
          <label className="form-label">FECHA FINAL</label>
          <input
            type="date"
            data-date-format="DD MMMM YYYY"
            className="form-control"
            selected={formInfo.fechaFinal}
            onChange={(date) => setFormInfo({ ...formInfo, fechaFinal: date.target.value })}
            style={{ width: "100%" }}
          />
        </div>
        <div className="col-2">
          <button
            type="button"
            className="btn btn-primary"
            style={{ top: "29px", position: "relative" }}
            onClick={generarReporte}
          >Consultar
          </button>
          <button
            className="btn btn-secondary"
            style={{ position: "relative", left: "8%", top: "30%" }}
            onClick={() => { setBusqueda(""); loadData(); setFormInfo({ fechaInicial: "", fechaFinal: "" }) }}
          >
            Limpiar
          </button>
        </div>
        <div className="col-3">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por folio, número de unidad, placas o vin..."
            style={{ position: "relative", left: "2%", top: "23%" }}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="col-3">
          <button
            className="btn btn-primary"
            style={{ position: "relative", left: "2%", top: "30%" }}
            onClick={() => loadData(1, busqueda, formInfo.fechaInicial, formInfo.fechaFinal)}
          >
            Buscar
          </button>
          <button
            className="btn btn-success"
            style={{ position: "relative", float: "right", top: "30%" }}
            onClick={handleExportCSV}
          >
            <FontAwesomeIcon
              icon={faFileCsv}
              className="fa-lg"
            />
          </button>
        </div>
        <div className="col-12">
          {
            registroEventosSeguridad.length !== 0 ? (
              <>
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="">
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th key={header.id}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id} style={{ textAlign: "center" }}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="position-relative">
                  <Paginacion paginado={paginado} onPageChange={handlePageChange} />
                </div>
              </>

            ) : (
              <>
                <center>
                  <h2>SIN DATOS QUE MOSTRAR</h2>
                </center>
              </>
            )
          }
        </div>
      </div>
    </>
  )
}